import api from 'src/app/api';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { buildEncodedQueryString } from 'src/common/utils/stringFunctions';
import { 
  Afsl,
  FetchTemplatesRequest, 
  Template,
  DuplicateTemplateRequest,
  GrandfatherTemplateRequest,
  UpdateTemplateRequest,
  CreateTemplateRequest,
  FetchAfslIdsResponse, 
  TemplateThunkResponse
} from '.';

export enum TemplateFeesActionTypes {
  FetchAfsls = '@@client/templates/fees/FetchAsflIds',
  FetchTemplates = '@@client/templates/fees/FetchTemplates',
  DuplicateTemplate = '@@client/templates/fees/DuplicateTemplate',
  GrandfatherTemplate = '@@client/templates/fees/GrandfatherTemplate',
  ManageTemplate = '@@client/templates/fees/ManageTemplate'
}

export enum TemplateFeesApiEndpoints {
  FetchAfsls = '/bff/GetAfslsPracticesAdvisersForUser',
  FetchTemplates = '/fees/GetAdviserFees',
  DuplicateTemplate = '/fees/DuplicateFee',
  GrandfatherTemplate = '/fees/GrandfatherFee',
  CreateTemplate = '/fees/CreateAdviserFee',
  UpdateTemplate = '/fees/UpdateAdviserFee'
}

export const fetchAfsls = createAsyncThunk<Afsl[]>(TemplateFeesActionTypes.FetchAfsls, async () => {
  const response = await api.get<FetchAfslIdsResponse>(TemplateFeesApiEndpoints.FetchAfsls);
  return response.data.afsls.map((a) => ({ id: a.afslId, name: a.afslName }));
});

export const fetchTemplates = createAsyncThunk<Template[], FetchTemplatesRequest>(
  TemplateFeesActionTypes.FetchTemplates,
  async (payload: FetchTemplatesRequest) => {
    const queryString = buildEncodedQueryString({
      afslId: payload.afslId,
    });
    const response = await api.get<Template[]>(`${TemplateFeesApiEndpoints.FetchTemplates}${queryString}`);
    return response.data;
  }
);

export const duplicateTemplate = createAsyncThunk(
  TemplateFeesActionTypes.DuplicateTemplate, 
  async (payload: DuplicateTemplateRequest, thunkApi) => {
  const body = {
    afslId: payload.afslId,
    feeId: payload.feeId
  };

  try {
    await api.post(`${TemplateFeesApiEndpoints.DuplicateTemplate}`, body);
    await thunkApi.dispatch(fetchTemplates({ afslId: payload.afslId }));
    return { message: 'Template duplicated' };
  } catch (error) {
    return { message: 'An error occured duplicating the template ', variant: 'error' };
  }
});

export const grandfatherTemplate = createAsyncThunk(
  TemplateFeesActionTypes.GrandfatherTemplate, 
  async (payload: GrandfatherTemplateRequest, thunkApi) => {
  try {
    await api.post(`${TemplateFeesApiEndpoints.GrandfatherTemplate}`, payload);
    await thunkApi.dispatch(fetchTemplates({ afslId: payload.afslId }));
    return { message: 'Template duplicated' };
  } catch (error) {
    return { message: 'An error occured with updating the template ', variant: 'error' };
  }
});

export const createTemplate = createAsyncThunk<TemplateThunkResponse, CreateTemplateRequest>(
  TemplateFeesActionTypes.ManageTemplate, 
  async (payload: CreateTemplateRequest, thunkApi) => {

  try {
    await api.post(`${TemplateFeesApiEndpoints.CreateTemplate}`, payload);
    await thunkApi.dispatch(fetchTemplates({ afslId: payload.afslId }));
    return { message: 'Template created' };
  } catch (error) {
    return { message: 'An error occured with creating the template ', variant: 'error' };
  }
});

export const updateTemplate = createAsyncThunk<TemplateThunkResponse, UpdateTemplateRequest>(
  TemplateFeesActionTypes.ManageTemplate, 
  async (payload: UpdateTemplateRequest, thunkApi) => {

  try {
    await api.post(`${TemplateFeesApiEndpoints.UpdateTemplate}`, payload);
    await thunkApi.dispatch(fetchTemplates({ afslId: payload.afslId }));
    return { message: 'Template updated' };
  } catch (error) {
    return { message: 'An error occured with updating the template ', variant: 'error' };
  }
});
